import { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import { Products } from '@/comm'
import { formatPercent } from '@/lib/format-utils'
import { exportToCsv } from '@/lib/utils'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DropdownMediaGroup } from '@/components/DropdownMediaGroup'
import { CalendarIcon } from '@radix-ui/react-icons'
import { format } from 'date-fns'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import dayjs from 'dayjs'
import { create } from 'zustand'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ROAS_STANDARD_DAY_OPTIONS } from '@/features/roas/constants/roas-days'

// Local date picker store for Report page
// const useReportDatePicker = create((set) => ({
//   date: dayjs().subtract(1, 'day').format('YYYYMMDD'),
//   updateDate: (date) => set({ date }),
// }))

const countryColumn = {
  id: 'country',
  header: 'Country',
  accessorFn: (row) => row.country,
  cell: (row) => {
    const country = row.getValue()
    const isTotal = country === 'total'
    return (
      <div className={cn('font-medium')}>
        {isTotal ? 'Total' : country.toUpperCase()}
      </div>
    )
  },
  meta: {
    sticky: true,
    stickyLeft: 'left-[0px]',
  },
}

// Helper function to determine color based on actual vs standard
const getColorClass = (actual, standard) => {
  // 未设定标准（标准为0或没有）使用黑色
  if (!standard || standard === 0) {
    return 'text-black'
  }
  
  // 高于或等于标准使用绿色
  if (actual >= standard) {
    return 'text-green-600'
  }
  
  // 计算差值（标准 - 实际值）
  const diff = standard - actual
  
  // 低于标准2%以内使用黄色
  if (diff <= 0.02) {
    return 'text-yellow-600'
  }
  
  // 低于标准2%以上使用红色（使用更深的红色以便区分）
  return 'text-red-700'
}

const PidCell = ({ item, pid, country, navigate, date, dayValue, mediaSource }) => {
  if (!item) {
    return <div className="text-gray-400 text-center">-</div>
  }

  const { actual, standard } = item
  const diff = actual - standard
  const diffStr = (diff > 0 ? '+' : '') + formatPercent(diff, 1)
  const targetStr = standard && standard > 0 ? formatPercent(standard, 1) : '-'
  const colorClass = getColorClass(actual, standard)

  const isClickable = pid.code === '14' && country === 'JP'

  const handleClick = () => {
    if (isClickable) {
      navigate(
        `/troas/report/detail?pid=${pid.code}&country=${country}&date=${date}&day=${dayValue}&mediaSource=${mediaSource}`,
      )
    }
  }

  return (
    <div
      className={cn(
        'text-right',
        isClickable && 'cursor-pointer hover:bg-muted/50 rounded p-1 -m-1',
      )}
      onClick={handleClick}
    >
      <div
        className={cn(
          'font-bold',
          colorClass,
          isClickable && 'underline decoration-dotted',
        )}
        title={`标准: ${targetStr}`}
      >
        {formatPercent(actual, 1)}
      </div>
      <div className="text-xs text-gray-500 whitespace-nowrap">
        {diffStr}/{targetStr}
      </div>
    </div>
  )
}

const pidColumn = (pid, data, navigate, date, dayValue, mediaSource) => ({
  id: `pid-report-${pid.code}`,
  header: `${pid.name}`,
  accessorFn: (row) => {
    return data.find(
      (d) => d.country === row.country && d.pid === parseInt(pid.code),
    )
  },
  cell: (row) => {
    return (
      <PidCell
        item={row.getValue()}
        pid={pid}
        country={row.row.original.country}
        navigate={navigate}
        date={date}
        dayValue={dayValue}
        mediaSource={mediaSource}
      />
    )
  },
})

import SingleDatePicker from './components/report-single-date-picker'

const Report = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // Initialize state from URL params or defaults
  // Before 5 PM (17:00), default to 2 days ago. After 5 PM, default to 1 day ago.
  const defaultDateOffset = dayjs().hour() < 17 ? 2 : 1
  const initialDate =
    searchParams.get('date') ||
    dayjs().subtract(defaultDateOffset, 'day').format('YYYYMMDD')
  const initialDay = searchParams.get('day') || '0'
  const initialMediaSource = searchParams.get('mediaSource') || 'app'

  const [date, setDate] = useState(initialDate)
  const [selectedDay, setSelectedDay] = useState(initialDay)
  const [mediaSource, setMediaSource] = useState(initialMediaSource)

  // Sync state to URL params
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        if (
          date !== newParams.get('date') ||
          selectedDay !== newParams.get('day') ||
          mediaSource !== newParams.get('mediaSource')
        ) {
            newParams.set('date', date)
            newParams.set('day', selectedDay)
            newParams.set('mediaSource', mediaSource)
            return newParams
        }
        return prev
      },
      { replace: true },
    )
  }, [date, selectedDay, mediaSource, setSearchParams])

  const dayValue = Number(selectedDay)

  const {
    data: response,
    isLoading,
    error,
    mutate,
    isValidating,
  } = useSWR([
    `/troas/standard-report`,
    { startDate: date, endDate: date, mediaSource, day_n: dayValue },
  ])
  const reportData = response?.data || []

  // Define custom product order (needed for standards description)
  const productOrder = [
    '1',
    '2',
    '3',
    '6',
    '14',
    '17',
    '4',
    '7',
    '5',
    '9',
    '13',
    '15',
    '11',
    '12',
    '16',
  ]

  // Standards description text with colored keywords
  const standardsDescription = (
    <>
      <span className="text-green-600 font-bold">绿色：</span>达到或超过标准；
      <span className="text-yellow-600 font-bold">黄色：</span>低于标准2%以内；
      <span className="text-red-700 font-bold">红色：</span>低于标准2%以上；
      <span className="text-black font-bold">黑色：</span>未设定标准
    </>
  )

  // Define country order
  const countryOrder = [
    'total',
    'BR',
    'JP',
    'MX',
    'DE',
    'US',
    'HK',
    'IT',
    'SA',
    'KR',
    'TW',
    'ES',
    'ZA',
    'ID',
    'GB',
    'FR',
    'TH',
    'AR',
    'CO',
    'SG',
    'MY',
    'CA',
    'AE',
  ]

  // Process data for country-based grouping
  const processedData = useMemo(() => {
    if (!reportData || reportData.length === 0) return []

    const uniqueCountries = [...new Set(reportData.map((d) => d.country))]
    const allCountries = uniqueCountries

    // Sort countries
    const sortedCountries = allCountries.sort((a, b) => {
      const indexA = countryOrder.indexOf(a)
      const indexB = countryOrder.indexOf(b)
      if (indexA === -1 && indexB === -1) return a.localeCompare(b)
      if (indexA === -1) return 1
      if (indexB === -1) return -1
      return indexA - indexB
    })

    return sortedCountries.map((country) => ({ country }))
  }, [reportData])

  const sortedProducts = useMemo(() => {
    const excludedProducts = [
      'total',
      'total1',
      'total1_1',
      'total1_2',
      'total2',
    ]
    return Products.filter(
      (product) => !excludedProducts.includes(product.code),
    ).sort((a, b) => {
      const indexA = productOrder.indexOf(a.code)
      const indexB = productOrder.indexOf(b.code)
      if (indexA === -1 && indexB === -1) return a.name.localeCompare(b.name)
      if (indexA === -1) return 1
      if (indexB === -1) return -1
      return indexA - indexB
    })
  }, [])

  // Build columns
  const columns = useMemo(() => {
    const cols = [countryColumn]
    sortedProducts.forEach((pid) => {
      cols.push(pidColumn(pid, reportData, navigate, date, dayValue, mediaSource))
    })
    return cols
  }, [reportData, sortedProducts, navigate, date, dayValue, mediaSource])

  const getRoasAsDecimalString = (value) =>
    typeof value === 'number' ? value.toFixed(4) : ''

  const handleExportCsv = () => {
    if (!reportData.length || !processedData.length) return

    const filename = `standard_report_${date}_day${dayValue}_${dayjs().format(
      'YYYYMMDDHHmmss',
    )}.csv`
    // 为每个产品创建三列：ROAS、标准 ROAS、ROAS Diff
    const headers = [
      'Country',
      ...sortedProducts.flatMap((p) => [
        `${p.name} ROAS`,
        `${p.name} 标准 ROAS`,
        `${p.name} ROAS Diff`,
      ]),
    ]

    const rows = processedData.map((row) => {
      const country = row.country
      const exportRow = {}

      exportRow.Country = country === 'total' ? 'Total' : country.toUpperCase()

      sortedProducts.forEach((product) => {
        const item = reportData.find(
          (d) => d.country === country && d.pid === parseInt(product.code),
        )
        if (item) {
          const { actual, standard } = item
          const diff = actual - standard
          const diffStr = (diff > 0 ? '+' : '') + formatPercent(diff, 1)
          const targetStr =
            standard && standard > 0 ? formatPercent(standard, 1) : '-'
          const actualStr = formatPercent(actual, 1)

          exportRow[`${product.name} ROAS`] = actualStr
          exportRow[`${product.name} 标准 ROAS`] = targetStr
          exportRow[`${product.name} ROAS Diff`] = diffStr
        } else {
          exportRow[`${product.name} ROAS`] = '-'
          exportRow[`${product.name} 标准 ROAS`] = '-'
          exportRow[`${product.name} ROAS Diff`] = '-'
        }
      })

      return exportRow
    })

    exportToCsv(rows, filename, headers)
  }

  const table = useReactTable({
    data: processedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-4 flex-wrap sm:gap-4 sm:py-2">
        <SingleDatePicker dateStr={date} onDateChange={setDate} />
        <DropdownMediaGroup value={mediaSource} update={setMediaSource} />
        <Select value={selectedDay} onValueChange={setSelectedDay}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Day 0" />
          </SelectTrigger>
          <SelectContent>
            {ROAS_STANDARD_DAY_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => mutate()}
          disabled={isValidating}
        >
          Refresh {isValidating && '...'}
        </Button>
        <Button variant="outline" onClick={handleExportCsv}>
          Export
        </Button>
      </div>

      {/* Standards Description */}
      <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
        <span className="font-semibold">标准说明：</span>{' '}
        {standardsDescription}
      </div>

      {/* Data Table */}
      <div className="relative min-h-[256px]">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center text-lg font-medium">
            Loading Report...
          </div>
        ) : error ? (
          <div className="flex h-64 items-center justify-center text-lg font-medium text-red-600">
            Failed to load data: {error?.message}
          </div>
        ) : (
          <DataTableWithBorder table={table} />
        )}
      </div>
    </div>
  )
}

export default Report
